import { z } from 'zod';

import {
	DirectionV10,
	TransactionStatusV10,
} from '../../../../../../../api/generated/cz.partners.bank.backofficegateway.transactions';
import { createDateSchemaWithDefinedMinAndMaxBankValues } from '../../../../../../../helpers/zod/createDateSchemaWithDefinedMinAndMaxBankValues';
import { createEnumArraySchema } from '../../../../../../../helpers/zod/createEnumArraySchema';
import { validateDateRange } from '../../../../../../../helpers/zod/validateDateRange';
import { PAYMENT_GROUPS } from '../../../../const';

export type ClientTransactionHistoryFilterFormErrorMessages = {
	amountFromHasToBeLessThanAmountToError: string;
	negativeAmountNotAllowedError: string;
	dateCantBeEarlierThanBankInceptionDateError: string;
	dateMustBeTodayAtLatestError: string;
	dateToMustBeBeforeDateFromError: string;
};

export const DEFAULT_MIN_TRANSACTION_AMOUNT = 0;

export const CLIENT_TRANSACTION_HISTORY_FILTER_DEFAULT_VALUES: ClientTransactionHistoryFilterForm =
	{
		transactionTimestampTo: null,
		transactionTimestampFrom: null,
		amountFrom: null,
		amountTo: null,
		directions: [],
		fullText: null,
		paymentGroups: [],
		bankCardUuid: null,
		transactionStatus: [],
	};

export const getClientTransactionHistoryFilterFormSchema = (
	errorMessages?: ClientTransactionHistoryFilterFormErrorMessages,
) => {
	const schema = z
		.object({
			transactionTimestampFrom: createDateSchemaWithDefinedMinAndMaxBankValues({
				minDateErrorMessage:
					errorMessages?.dateCantBeEarlierThanBankInceptionDateError,
				maxDateErrorMessage: errorMessages?.dateMustBeTodayAtLatestError,
			}),
			transactionTimestampTo: createDateSchemaWithDefinedMinAndMaxBankValues({
				minDateErrorMessage:
					errorMessages?.dateCantBeEarlierThanBankInceptionDateError,
				maxDateErrorMessage: errorMessages?.dateMustBeTodayAtLatestError,
			}),
			amountFrom: z
				.number()
				.min(DEFAULT_MIN_TRANSACTION_AMOUNT, {
					message: errorMessages?.negativeAmountNotAllowedError,
				})
				.optional()
				.nullable(),
			amountTo: z
				.number()
				.min(DEFAULT_MIN_TRANSACTION_AMOUNT, {
					message: errorMessages?.negativeAmountNotAllowedError,
				})
				.optional()
				.nullable(),
			directions: createEnumArraySchema(DirectionV10),
			fullText: z.string().optional().nullable(),
			paymentGroups: createEnumArraySchema(PAYMENT_GROUPS),
			bankCardUuid: z.string().optional().nullable(),
			transactionStatus: createEnumArraySchema(TransactionStatusV10),
		})
		.superRefine(
			(
				{
					amountFrom,
					amountTo,
					transactionTimestampFrom,
					transactionTimestampTo,
				},
				ctx,
			) => {
				if (amountTo && amountFrom) {
					if (amountFrom > amountTo) {
						ctx.addIssue({
							code: 'custom',
							message: errorMessages?.amountFromHasToBeLessThanAmountToError,
							path: ['amountFrom'],
						});
					}
				}

				validateDateRange(
					transactionTimestampFrom,
					transactionTimestampTo,
					errorMessages?.dateToMustBeBeforeDateFromError,
					ctx,
					'transactionTimestampFrom',
				);
			},
		);

	return schema;
};
export type ClientTransactionHistoryFilterForm = z.infer<
	ReturnType<typeof getClientTransactionHistoryFilterFormSchema>
>;
